/**
 * CaseFileDetail Page
 *
 * Displays full CaseFile details with edit capability for owners.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

type CaseFilePermission = 'owner' | 'editor' | 'logger' | 'viewer';
type CareTeamRelationship = 'primary' | 'secondary' | 'medical' | 'educational' | 'coordinator' | 'professional';

interface CareTeamMember {
  userId: string;
  relationship: CareTeamRelationship;
  permissions: CaseFilePermission;
  name?: string;
  email?: string;
}

interface CaseFile {
  caseFileId: string;
  name: string;
  dateOfBirth: string;
  diagnoses: string[];
  medications: string[];
  insuranceInfo: {
    provider: string;
    policyNumber: string;
    groupNumber?: string;
  };
  schoolInfo?: {
    schoolName: string;
    grade?: string;
    hasIEP: boolean;
    has504Plan: boolean;
  };
  ownerId: string;
  careTeam: CareTeamMember[];
}

interface GetCaseFileResponse {
  success: boolean;
  caseFile: CaseFile;
  userPermissions: CaseFilePermission;
  userRelationship?: CareTeamRelationship;
  isOwner: boolean;
}

interface UpdateCaseFileResponse {
  success: boolean;
  message: string;
  updatedFields: string[];
}

interface FormErrors {
  name?: string;
  dateOfBirth?: string;
  diagnoses?: string;
  insuranceProvider?: string;
  policyNumber?: string;
}

function getPermissionBadgeClass(permission: CaseFilePermission): string {
  switch (permission) {
    case 'owner':
      return 'badge badge-success';
    case 'editor':
      return 'badge badge-primary';
    case 'logger':
      return 'badge badge-secondary';
    case 'viewer':
      return 'badge';
    default:
      return 'badge';
  }
}

function formatRelationship(relationship: CareTeamRelationship): string {
  const labels: Record<CareTeamRelationship, string> = {
    primary: 'Primary Caregiver',
    secondary: 'Secondary Caregiver',
    medical: 'Medical Provider',
    educational: 'School Staff',
    coordinator: 'Care Coordinator',
    professional: 'Professional Caretaker',
  };
  return labels[relationship] || relationship;
}

function formatPermission(permission: CaseFilePermission): string {
  return permission.charAt(0).toUpperCase() + permission.slice(1);
}

export function CaseFileDetail(): JSX.Element {
  const { caseFileId } = useParams<{ caseFileId: string }>();
  const navigate = useNavigate();

  // Data state
  const [caseFile, setCaseFile] = useState<CaseFile | null>(null);
  const [userPermissions, setUserPermissions] = useState<CaseFilePermission>('viewer');
  const [isOwner, setIsOwner] = useState(false);

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Form state (for edit mode)
  const [formData, setFormData] = useState<Partial<CaseFile>>({});
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const fetchCaseFile = async () => {
    if (!caseFileId) return;

    setLoading(true);
    setError(null);

    try {
      const getCaseFile = httpsCallable<{ caseFileId: string }, GetCaseFileResponse>(
        functions,
        'getCaseFile'
      );
      const result = await getCaseFile({ caseFileId });

      if (result.data.success) {
        setCaseFile(result.data.caseFile);
        setUserPermissions(result.data.userPermissions);
        setIsOwner(result.data.isOwner);
      } else {
        setError('Failed to load CaseFile');
      }
    } catch (err) {
      console.error('Error fetching CaseFile:', err);
      const rawMessage = err instanceof Error ? err.message : '';
      if (rawMessage.toLowerCase().includes('not found') || rawMessage.toLowerCase().includes('does not have access')) {
        setError('This care record could not be found. It may have been removed or you may not have access.');
      } else {
        setError('Something went wrong while loading this care record. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCaseFile();
  }, [caseFileId]);

  const startEditing = () => {
    if (!caseFile) return;
    setFormData({
      name: caseFile.name,
      dateOfBirth: caseFile.dateOfBirth,
      diagnoses: [...caseFile.diagnoses],
      medications: [...(caseFile.medications || [])],
      insuranceInfo: { ...caseFile.insuranceInfo },
      schoolInfo: caseFile.schoolInfo ? { ...caseFile.schoolInfo } : undefined,
    });
    setFormErrors({});
    setSaveError(null);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setFormData({});
    setFormErrors({});
    setSaveError(null);
    setIsEditing(false);
  };

  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    if (!formData.name?.trim()) {
      errors.name = 'Name is required';
    }

    if (!formData.dateOfBirth?.trim()) {
      errors.dateOfBirth = 'Date of birth is required';
    } else {
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(formData.dateOfBirth)) {
        errors.dateOfBirth = 'Date must be in YYYY-MM-DD format';
      } else {
        const date = new Date(formData.dateOfBirth);
        if (isNaN(date.getTime())) {
          errors.dateOfBirth = 'Invalid date';
        } else if (date > new Date()) {
          errors.dateOfBirth = 'Date cannot be in the future';
        }
      }
    }

    if (!formData.diagnoses || formData.diagnoses.length === 0 || !formData.diagnoses.some(d => d.trim())) {
      errors.diagnoses = 'At least one diagnosis is required';
    }

    if (!formData.insuranceInfo?.provider?.trim()) {
      errors.insuranceProvider = 'Insurance provider is required';
    }

    if (!formData.insuranceInfo?.policyNumber?.trim()) {
      errors.policyNumber = 'Policy number is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!caseFileId || !validateForm()) return;

    setSaving(true);
    setSaveError(null);

    try {
      const updateCaseFile = httpsCallable<
        { caseFileId: string; updates: Partial<CaseFile> },
        UpdateCaseFileResponse
      >(functions, 'updateCaseFile');

      // Check if schoolInfo has any actual data
      const hasSchoolInfo =
        formData.schoolInfo?.schoolName?.trim() ||
        formData.schoolInfo?.grade?.trim() ||
        formData.schoolInfo?.hasIEP ||
        formData.schoolInfo?.has504Plan;

      const updates: Partial<CaseFile> = {
        name: formData.name?.trim(),
        dateOfBirth: formData.dateOfBirth?.trim(),
        diagnoses: formData.diagnoses?.filter(d => d.trim()),
        medications: formData.medications?.filter(m => m.trim()),
        insuranceInfo: {
          provider: formData.insuranceInfo?.provider?.trim() || '',
          policyNumber: formData.insuranceInfo?.policyNumber?.trim() || '',
          groupNumber: formData.insuranceInfo?.groupNumber?.trim() || undefined,
        },
      };

      // Only include schoolInfo if it has actual data (don't send null/empty)
      if (hasSchoolInfo) {
        updates.schoolInfo = {
          schoolName: formData.schoolInfo?.schoolName?.trim() || '',
          grade: formData.schoolInfo?.grade?.trim() || undefined,
          hasIEP: Boolean(formData.schoolInfo?.hasIEP),
          has504Plan: Boolean(formData.schoolInfo?.has504Plan),
        };
      }

      const result = await updateCaseFile({ caseFileId, updates });

      if (result.data.success) {
        // Refresh data
        await fetchCaseFile();
        setIsEditing(false);
      } else {
        setSaveError('Failed to save changes');
      }
    } catch (err) {
      console.error('Error saving CaseFile:', err);
      setSaveError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setSaving(false);
    }
  };

  const updateFormField = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateInsuranceField = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      insuranceInfo: {
        provider: prev.insuranceInfo?.provider || '',
        policyNumber: prev.insuranceInfo?.policyNumber || '',
        ...prev.insuranceInfo,
        [field]: value,
      },
    }));
  };

  const updateSchoolField = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      schoolInfo: {
        schoolName: prev.schoolInfo?.schoolName || '',
        hasIEP: prev.schoolInfo?.hasIEP || false,
        has504Plan: prev.schoolInfo?.has504Plan || false,
        ...prev.schoolInfo,
        [field]: value,
      },
    }));
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-airy flex items-center justify-center">
        <div className="text-lg text-muted">Loading CaseFile...</div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-airy">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="alert alert-error flex justify-between items-center">
            <span>Error: {error}</span>
            <div className="flex gap-2">
              <button onClick={fetchCaseFile} className="btn-ghost btn-sm">
                Retry
              </button>
              <button onClick={() => navigate('/')} className="btn-ghost btn-sm">
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!caseFile) {
    return (
      <div className="min-h-screen bg-airy flex items-center justify-center">
        <div className="card p-8 max-w-md text-center">
          <h2 className="text-lg font-semibold text-ink mb-2">Care Record Not Available</h2>
          <p className="text-muted mb-6">This care record could not be found. It may have been removed or you may not have access.</p>
          <button onClick={() => navigate('/')} className="btn-primary">
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-airy">
      {/* Header */}
      <header className="bg-white border-b border-soft-neutral">
        <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="btn-ghost btn-sm"
            >
              &larr; Back
            </button>
            <h1 className="text-xl font-semibold text-ink">
              {isEditing ? 'Edit CaseFile' : caseFile.name}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <span className={getPermissionBadgeClass(userPermissions)}>
              You: {formatPermission(userPermissions)}
            </span>
            {isOwner && !isEditing && (
              <button onClick={startEditing} className="btn-primary">
                Edit
              </button>
            )}
            {isEditing && (
              <>
                <button
                  onClick={cancelEditing}
                  className="btn-ghost"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="btn-primary"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      <main id="main-content" tabIndex={-1} className="max-w-4xl mx-auto px-4 py-8 outline-none">
        {/* Save error banner */}
        {saveError && (
          <div className="mb-6 alert alert-error flex justify-between items-center">
            <span>Error saving: {saveError}</span>
            <button
              onClick={() => setSaveError(null)}
              className="text-red-700 hover:text-red-900 text-xl leading-none"
            >
              &times;
            </button>
          </div>
        )}

        <div className="space-y-6">
          {/* Basic Info Section */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-ink">Basic Information</h2>
            </div>
            <div className="card-body space-y-4">
              {isEditing ? (
                <>
                  <div>
                    <label className="label">Name *</label>
                    <input
                      type="text"
                      value={formData.name || ''}
                      onChange={(e) => updateFormField('name', e.target.value)}
                      className={`input ${formErrors.name ? 'input-error' : ''}`}
                    />
                    {formErrors.name && <p className="error-text">{formErrors.name}</p>}
                  </div>
                  <div>
                    <label className="label">Date of Birth *</label>
                    <input
                      type="date"
                      value={formData.dateOfBirth || ''}
                      onChange={(e) => updateFormField('dateOfBirth', e.target.value)}
                      className={`input ${formErrors.dateOfBirth ? 'input-error' : ''}`}
                    />
                    {formErrors.dateOfBirth && <p className="error-text">{formErrors.dateOfBirth}</p>}
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <span className="text-sm text-muted">Name</span>
                    <p className="text-ink font-medium">{caseFile.name}</p>
                  </div>
                  <div>
                    <span className="text-sm text-muted">Date of Birth</span>
                    <p className="text-ink font-medium">{caseFile.dateOfBirth}</p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Diagnoses Section */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-ink">Diagnoses</h2>
            </div>
            <div className="card-body">
              {isEditing ? (
                <div>
                  <label className="label">Diagnoses * (one per line)</label>
                  <textarea
                    value={(formData.diagnoses || []).join('\n')}
                    onChange={(e) => updateFormField('diagnoses', e.target.value.split('\n'))}
                    className={`input min-h-[100px] ${formErrors.diagnoses ? 'input-error' : ''}`}
                    placeholder="Enter each diagnosis on a new line"
                  />
                  {formErrors.diagnoses && <p className="error-text">{formErrors.diagnoses}</p>}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {caseFile.diagnoses.map((diagnosis, idx) => (
                    <span key={idx} className="badge badge-secondary">
                      {diagnosis}
                    </span>
                  ))}
                  {caseFile.diagnoses.length === 0 && (
                    <span className="text-muted">No diagnoses recorded</span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Medications Section */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-ink">Medications</h2>
            </div>
            <div className="card-body">
              {isEditing ? (
                <div>
                  <label className="label">Medications (one per line)</label>
                  <textarea
                    value={(formData.medications || []).join('\n')}
                    onChange={(e) => updateFormField('medications', e.target.value.split('\n'))}
                    className="input min-h-[100px]"
                    placeholder="Enter each medication on a new line"
                  />
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {(caseFile.medications || []).map((medication, idx) => (
                    <span key={idx} className="badge">
                      {medication}
                    </span>
                  ))}
                  {(!caseFile.medications || caseFile.medications.length === 0) && (
                    <span className="text-muted">No medications recorded</span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Insurance Section */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-ink">Insurance Information</h2>
            </div>
            <div className="card-body space-y-4">
              {isEditing ? (
                <>
                  <div>
                    <label className="label">Provider *</label>
                    <input
                      type="text"
                      value={formData.insuranceInfo?.provider || ''}
                      onChange={(e) => updateInsuranceField('provider', e.target.value)}
                      className={`input ${formErrors.insuranceProvider ? 'input-error' : ''}`}
                    />
                    {formErrors.insuranceProvider && <p className="error-text">{formErrors.insuranceProvider}</p>}
                  </div>
                  <div>
                    <label className="label">Policy Number *</label>
                    <input
                      type="text"
                      value={formData.insuranceInfo?.policyNumber || ''}
                      onChange={(e) => updateInsuranceField('policyNumber', e.target.value)}
                      className={`input ${formErrors.policyNumber ? 'input-error' : ''}`}
                    />
                    {formErrors.policyNumber && <p className="error-text">{formErrors.policyNumber}</p>}
                  </div>
                  <div>
                    <label className="label">Group Number</label>
                    <input
                      type="text"
                      value={formData.insuranceInfo?.groupNumber || ''}
                      onChange={(e) => updateInsuranceField('groupNumber', e.target.value)}
                      className="input"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <span className="text-sm text-muted">Provider</span>
                    <p className="text-ink font-medium">{caseFile.insuranceInfo.provider}</p>
                  </div>
                  <div>
                    <span className="text-sm text-muted">Policy Number</span>
                    <p className="text-ink font-medium">{caseFile.insuranceInfo.policyNumber}</p>
                  </div>
                  {caseFile.insuranceInfo.groupNumber && (
                    <div>
                      <span className="text-sm text-muted">Group Number</span>
                      <p className="text-ink font-medium">{caseFile.insuranceInfo.groupNumber}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* School Info Section */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-ink">School Information</h2>
            </div>
            <div className="card-body space-y-4">
              {isEditing ? (
                <>
                  <div>
                    <label className="label">School Name</label>
                    <input
                      type="text"
                      value={formData.schoolInfo?.schoolName || ''}
                      onChange={(e) => updateSchoolField('schoolName', e.target.value)}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="label">Grade</label>
                    <input
                      type="text"
                      value={formData.schoolInfo?.grade || ''}
                      onChange={(e) => updateSchoolField('grade', e.target.value)}
                      className="input"
                    />
                  </div>
                  <div className="flex gap-6">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.schoolInfo?.hasIEP || false}
                        onChange={(e) => updateSchoolField('hasIEP', e.target.checked)}
                        className="h-4 w-4 rounded border-soft-neutral text-evergreen focus:ring-evergreen"
                      />
                      <span className="text-sm text-ink">Has IEP</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.schoolInfo?.has504Plan || false}
                        onChange={(e) => updateSchoolField('has504Plan', e.target.checked)}
                        className="h-4 w-4 rounded border-soft-neutral text-evergreen focus:ring-evergreen"
                      />
                      <span className="text-sm text-ink">Has 504 Plan</span>
                    </label>
                  </div>
                </>
              ) : caseFile.schoolInfo ? (
                <>
                  <div>
                    <span className="text-sm text-muted">School Name</span>
                    <p className="text-ink font-medium">{caseFile.schoolInfo.schoolName}</p>
                  </div>
                  {caseFile.schoolInfo.grade && (
                    <div>
                      <span className="text-sm text-muted">Grade</span>
                      <p className="text-ink font-medium">{caseFile.schoolInfo.grade}</p>
                    </div>
                  )}
                  <div className="flex gap-4">
                    {caseFile.schoolInfo.hasIEP && (
                      <span className="badge badge-primary">Has IEP</span>
                    )}
                    {caseFile.schoolInfo.has504Plan && (
                      <span className="badge badge-primary">Has 504 Plan</span>
                    )}
                    {!caseFile.schoolInfo.hasIEP && !caseFile.schoolInfo.has504Plan && (
                      <span className="text-muted">No IEP or 504 Plan</span>
                    )}
                  </div>
                </>
              ) : (
                <span className="text-muted">No school information recorded</span>
              )}
            </div>
          </div>

          {/* Care Team Section */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-ink">Care Team</h2>
            </div>
            <div className="card-body">
              {caseFile.careTeam && caseFile.careTeam.length > 0 ? (
                <div className="space-y-3">
                  {caseFile.careTeam.map((member, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 bg-soft-neutral rounded-md"
                    >
                      <div>
                        <p className="text-ink font-medium">
                          {member.name || member.email || member.userId}
                        </p>
                        <p className="text-sm text-muted">
                          {formatRelationship(member.relationship)}
                        </p>
                      </div>
                      <span className={getPermissionBadgeClass(member.permissions)}>
                        {formatPermission(member.permissions)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <span className="text-muted">No care team members</span>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default CaseFileDetail;
